import { Classes, Dialog } from "@blueprintjs/core";
import * as React from "react";
import styled from "styled-components";
import { InscriptionAbandonDto, InscriptionApi } from "../../../../../api";
import * as Yup from "yup";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { FGWalterDateMaskInput, SmallFormGenerator } from "../../../../../components";
import { FieldGroup } from "nsitools-react";
import { format, isBefore, isSameDay, isToday } from "date-fns";

const StyledDialog = styled(Dialog)`
  width: 500px;
  height: auto;
  background-color: white;
`;

export interface IInscriptionAbandonDetailDialogProps {
  dialogOpen: boolean;
  onClose: (toSave?: InscriptionAbandonDto) => void;
  currentAbandon: InscriptionAbandonDto;
}

export const InscriptionAbandonDetailDialog: React.FunctionComponent<IInscriptionAbandonDetailDialogProps> = ({
  currentAbandon,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(InscriptionApi);

  const hasDefaultDate = React.useMemo(()=>!!currentAbandon?.dateAbandon,[currentAbandon?.dateAbandon]);
  
  const { data, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => currentAbandon,
        saveApiFn: async d => {
          await api.inscriptionAbandon({ InscriptionAbandonDto: d });
          return d;
        },
        onSaved: d => onClose(d),
        serverValidationRootKey: "Dto"
      }),
      [api, currentAbandon, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        dateAbandon: Yup.date()
          .nullable()
          .test("default-min-date", t(ETLCodes.DateCannotBeBefore, {date: currentAbandon?.dateAbandon && format(currentAbandon?.dateAbandon, "dd-MM-yyyy")}), function(value: Date) {
            return !hasDefaultDate || isBefore(currentAbandon?.dateAbandon, value) || isSameDay(currentAbandon?.dateAbandon, value);
          })
          .required(t(ETLCodes.Required))
      }),
    [currentAbandon?.dateAbandon, hasDefaultDate, t]
  );

  return (
    <StyledDialog title={t(ETLCodes.AbandonInscription)} isOpen={dialogOpen} onClose={() => onClose()}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose()}
          showDeleteButton={false}
          saving={saving}
          minLabelWidth={100}
          validationErrors={validationErrors}
          preventDefaultSaveOnEnter
          forceEnableSave={hasDefaultDate}
        >
          <FieldGroup>
            <FGWalterDateMaskInput name="dateAbandon" label={t(ETLCodes.DateAbandon)} minDate={currentAbandon?.dateAbandon} helperText={hasDefaultDate && t(ETLCodes.DateParDefautDateSortieEnCours)} />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
